import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Modal component that confirms a payment link has been sent, displays the recipient phone number, and manages a resend/cancel workflow with a countdown timer.
 * @component
 * @selector app-payment-link-sent-modal
 * @example
 *   <app-payment-link-sent-modal
 *     [isOpen]="true"
 *     [phoneNumber]="'12345678901'"
 *     [isResending]="false"
 *     (close)="onClose()"
 *     (cancelPayment)="onCancelPayment()"
 *     (resendLink)="onResendLink()">
 *   </app-payment-link-sent-modal>
 * @input boolean isOpen - Controls whether the modal is visible; when true the countdown timer is started or reset.
 * @input string phoneNumber - The recipient phone number to display in the modal; formatted for readability.
 * @input boolean isResending - Indicates whether a resend operation is in progress (affects UI state and interactions).
 * @output EventEmitter<void> close - Emitted when the modal is closed either manually or automatically when the timer finishes.
 * @output EventEmitter<void> cancelPayment - Emitted when the user chooses to cancel the payment from the modal.
 * @output EventEmitter<void> resendLink - Emitted when the user requests that the payment link be resent.
 *
 * Key methods:
 * - startTimer(): Starts a 5:00 countdown, decrementing seconds and minutes every second and handling timer completion.
 * - clearTimer(): Stops and clears the active interval to prevent multiple intervals and memory leaks.
 * - resetTimer(): Resets the countdown to the default 5 minutes and 0 seconds.
 * - onTimerFinished(): Handler invoked when the timer reaches zero; triggers auto-close behavior.
 * - closeModal(): Clears the timer and emits the close event to notify parent components.
 * - onCancelPayment(): Clears the timer and emits the cancelPayment event to notify parent components.
 * - onResendLink(): Resets and restarts the timer and emits the resendLink event to trigger resending logic.
 * - formattedMinutes / formattedSeconds (getters): Provide zero-padded minute and second strings for display.
 * - formattedPhoneNumber (getter): Returns the phone number formatted for display (e.g., +X (XXX) XXX XXXX).
 *
 * Important lifecycle hooks:
 * - ngOnInit(): If the modal is open on initialization, starts the countdown timer.
 * - ngOnChanges(): Responds to changes to isOpen by resetting/starting or clearing the timer as appropriate.
 * - ngOnDestroy(): Ensures the timer is cleared when the component is destroyed to avoid resource leaks.
 */
@Component({
  selector: 'app-payment-link-sent-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './payment-link-sent-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./payment-link-sent-modal.component.scss'],
})
export class PaymentLinkSentModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Input() phoneNumber = '';
  @Input() isResending = false;
  @Output() close = new EventEmitter<void>();
  @Output() cancelPayment = new EventEmitter<void>();
  @Output() resendLink = new EventEmitter<void>();

  // Timer properties
  minutes = 5;
  seconds = 0;
  private timerInterval: any;

  ngOnInit(): void {
    if (this.isOpen) {
      this.startTimer();
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  ngOnChanges(): void {
    if (this.isOpen) {
      this.resetTimer();
      this.startTimer();
    } else {
      this.clearTimer();
    }
  }

  /**
   * Starts a countdown timer that clears any existing timer, decrements minutes and seconds every second, and calls onTimerFinished when the countdown reaches zero.
   * @example
   * this.startTimer()
   * // undefined
   * @returns {void} No return value.
   */
  private startTimer(): void {
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.seconds > 0) {
        this.seconds--;
      } else if (this.minutes > 0) {
        this.minutes--;
        this.seconds = 59;
      } else {
        // Timer finished
        this.clearTimer();
        this.onTimerFinished();
      }
    }, 1000);
  }

  private clearTimer(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private resetTimer(): void {
    this.minutes = 5;
    this.seconds = 0;
  }

  private onTimerFinished(): void {
    // Auto-close modal when timer finishes
    this.closeModal();
  }

  closeModal(): void {
    this.clearTimer();
    this.close.emit();
  }

  onCancelPayment(): void {
    this.clearTimer();
    this.cancelPayment.emit();
  }

  onResendLink(): void {
    this.resetTimer();
    this.startTimer();
    this.resendLink.emit();
  }

  get formattedMinutes(): string {
    return this.minutes.toString().padStart(2, '0');
  }

  get formattedSeconds(): string {
    return this.seconds.toString().padStart(2, '0');
  }

  get formattedPhoneNumber(): string {
    if (!this.phoneNumber) return '';
    // Format phone number for display
    return this.phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3 $4');
  }
}
